import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";

export const SecurityPanel = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [hasPassword, setHasPassword] = useState(null);
  const [pw, setPw] = useState({ a: "", b: "" });
  const [saving, setSaving] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  useEffect(() => { api.get("/auth/password").then(({ data }) => { setHasPassword(data.has_password); setTwoFactor(data.admin_two_factor); }); }, []);
  const toggleTwoFactor = async (enabled) => {
    try { await api.put("/auth/two-factor", { enabled }); setTwoFactor(enabled); toast.success(t(enabled ? "Two-factor sign-in is on for Admins" : "Two-factor sign-in is off for Admins")); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error(t("Passwords don't match"));
    setSaving(true);
    try {
      await api.put("/auth/password", { password: pw.a });
      setHasPassword(true);
      setPw({ a: "", b: "" });
      toast.success(t("Password saved"));
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("Remove the backup password?"))) return;
    try { await api.delete("/auth/password"); setHasPassword(false); toast.success(t("Password removed")); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  if (hasPassword === null) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="max-w-2xl space-y-4">
    <div className="flex items-start gap-4 rounded-3xl border border-slate-200/80 bg-white p-5" data-testid="security-two-factor">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white"><ShieldCheck className="h-5 w-5" /></span>
      <div className="flex-1">
        <h2 className="font-display text-lg font-bold text-slate-900">{t("Two-factor sign-in for Admins")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("When on, Admins get a 6-digit code by email after signing in with Google or the backup password.")}</p>
      </div>
      <Switch checked={twoFactor} onCheckedChange={toggleTwoFactor} data-testid="security-two-factor-switch" />
    </div>
    <form onSubmit={save} className="space-y-5 rounded-3xl border border-slate-200/80 bg-white p-6" data-testid="security-panel">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white"><KeyRound className="h-5 w-5" /></span>
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">{t("Backup password sign-in")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("If Google sign-in stops working, the owner can sign in with this email and a password.")}</p>
          <p dir="ltr" className="mt-2 text-start font-mono text-sm text-slate-700">{user.email}</p>
        </div>
      </div>
      <p className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${hasPassword ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="security-password-status">
        <ShieldCheck className="h-3.5 w-3.5" /> {hasPassword ? t("Password is set") : t("No password yet")}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input type="password" dir="ltr" required minLength={10} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })}
          placeholder={t("New password (at least 10 characters)")} className="h-12 rounded-xl" data-testid="security-password-input" autoComplete="new-password" />
        <Input type="password" dir="ltr" required minLength={10} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })}
          placeholder={t("Confirm password")} className="h-12 rounded-xl" data-testid="security-password-confirm" autoComplete="new-password" />
      </div>
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={saving} data-testid="security-password-save"
          className="btn-press min-h-[50px] rounded-2xl bg-slate-900 px-6 font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : hasPassword ? t("Change password") : t("Set password")}
        </button>
        {hasPassword && <button type="button" onClick={remove} data-testid="security-password-remove"
          className="btn-press min-h-[50px] rounded-2xl px-5 font-semibold text-slate-500 hover:text-wade">{t("Remove password")}</button>}
      </div>
    </form>
    </div>
  );
};
